import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

/** Rigid hand props (Meshy image-to-3D) parented to the hand bone, so they stay held in every animation. */
export const PROP_SPEC = { naya: { bone: "LeftHand", type: "staff", k: 1.02 }, elder: { bone: "RightHand", type: "staff", k: 1.04 }, artisan: { bone: "LeftHand", type: "pot", k: 0.2 }, musician: { bone: "LeftHand", type: "pot", k: 0.3 } };
// Characters whose original Meshy body already contains a (badly attached) prop: only their "bare" version gets the rigid prop.
const FUSED = ["naya", "musician"];

export const propFor = (st, job) => (st?.prop?.status === "READY" && job && job.source !== "upload" && job.variant !== "props" && !(FUSED.includes(st.character) && job.variant !== "bare") ? st.prop.file_id : null);

const cache = {};
const loadProp = (fid) => (cache[fid] ||= new GLTFLoader().loadAsync(`${process.env.REACT_APP_BACKEND_URL}/api/characters3d/files/${fid}`));
const bone = (root, n) => { let b = null; root.traverse((o) => { if (!b && o.isBone && (o.name === n || o.name.endsWith(`:${n}`))) b = o; }); return b; };

export async function attachProp(scene, character, fid, idle) {
  const spec = PROP_SPEC[character], hand = spec && bone(scene, spec.bone);
  if (!hand || scene.userData.prop) return;
  const prop = (typeof fid === "string" ? await loadProp(fid) : fid).scene.clone(true);
  if (idle) { const m = new THREE.AnimationMixer(scene); m.clipAction(idle).play(); m.update(0); }
  scene.updateMatrixWorld(true);
  const body = new THREE.Box3().setFromObject(scene), H = body.max.y - body.min.y;
  const finger = hand.children.find((c) => c.isBone);
  const palmAt = (v = new THREE.Vector3()) => { hand.getWorldPosition(v); return finger ? v.lerp(finger.getWorldPosition(new THREE.Vector3()), 0.55) : v; };
  const palm = palmAt();
  const hips = bone(scene, "Hips")?.getWorldPosition(new THREE.Vector3()) || new THREE.Vector3();
  const pb = new THREE.Box3().setFromObject(prop);
  prop.scale.setScalar((spec.k * H) / (pb.max.y - pb.min.y || 1));
  prop.updateMatrixWorld(true);
  const b = new THREE.Box3().setFromObject(prop), c = b.getCenter(new THREE.Vector3());
  if (spec.type === "staff") prop.position.add(new THREE.Vector3(palm.x - c.x, body.min.y - b.min.y, palm.z - c.z));
  else {
    const out = Math.sign(palm.x - hips.x) || 1;
    prop.position.add(new THREE.Vector3(palm.x + out * 0.03 * H - c.x, palm.y + 0.05 * H - c.y, palm.z + 0.02 * H - c.z));
  }
  prop.traverse((o) => { if (o.isMesh) { o.castShadow = true; o.frustumCulled = false; } });
  prop.updateMatrixWorld(true);
  scene.userData.prop = prop;
  if (spec.type !== "staff") { hand.attach(prop); return; }
  // Walking stick: follows the palm but stays upright, like a real staff held in the hand.
  scene.attach(prop);
  const grip = prop.position.clone().sub(scene.worldToLocal(palm.clone())), tmp = new THREE.Vector3();
  scene.userData.updateProp = () => { hand.updateWorldMatrix(true, true); prop.position.copy(scene.worldToLocal(palmAt(tmp))).add(grip); };
}
